import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldCheck, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// gtag/__loadMetaPixel/__loadClarity los inyecta index.html — no vienen tipados por defecto en window
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    __loadMetaPixel?: () => void;
    __loadClarity?: () => void;
  }
}

const COOKIE_KEY = 'ruray_cookie_consent';

const CookieConsent: React.FC = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_KEY);
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const accept = () => {
    localStorage.setItem(COOKIE_KEY, 'accepted');
    setVisible(false);
    if (typeof window === 'undefined') return;
    window.gtag?.('consent', 'update', {
      analytics_storage: 'granted',
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
    });
    // A diferencia de GA4 (gateado por Consent Mode), Meta Pixel y Clarity
    // solo empiezan a trackear cuando se les llama — recién aquí, al aceptar.
    window.__loadMetaPixel?.();
    window.__loadClarity?.();
  };

  const decline = () => {
    localStorage.setItem(COOKIE_KEY, 'declined');
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 40, opacity: 0, filter: 'blur(8px)' }}
          animate={{ y: 0, opacity: 1, filter: 'blur(0px)' }}
          exit={{ y: 24, opacity: 0, filter: 'blur(6px)' }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          style={{
            position: 'fixed',
            bottom: '1.5rem',
            left: '1.5rem',
            right: '1.5rem',
            maxWidth: 460,
            zIndex: 9998,
          }}
        >
          <div
            style={{
              position: 'relative',
              background: 'linear-gradient(180deg, rgba(16,16,18,0.92) 0%, rgba(8,8,9,0.96) 100%)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: '1px solid rgba(255,255,255,0.09)',
              borderRadius: 18,
              padding: '1.6rem 1.6rem 1.4rem',
              boxShadow: '0 24px 60px -16px rgba(0,0,0,0.65), 0 0 0 1px rgba(0,229,209,0.04)',
              overflow: 'hidden',
            }}
          >
            {/* Filete superior con el degradado de marca — sutil, no un botón, solo firma visual */}
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 2,
                background: 'linear-gradient(90deg, var(--accent-teal), var(--accent-purple))',
                opacity: 0.9,
              }}
            />

            <button
              onClick={decline}
              aria-label={t('cookie_consent.close_aria')}
              style={{
                position: 'absolute',
                top: 14,
                right: 14,
                width: 26,
                height: 26,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.06)',
                borderRadius: '50%',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                transition: 'background 0.25s ease, color 0.25s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.09)';
                e.currentTarget.style.color = 'var(--text-secondary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                e.currentTarget.style.color = 'var(--text-muted)';
              }}
            >
              <X size={13} />
            </button>

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: '1.3rem' }}>
              <div
                aria-hidden="true"
                style={{
                  flexShrink: 0,
                  width: 36,
                  height: 36,
                  borderRadius: 11,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'radial-gradient(circle at 30% 30%, rgba(0,229,209,0.22), rgba(139,92,246,0.10) 70%)',
                  border: '1px solid rgba(0,229,209,0.18)',
                }}
              >
                <ShieldCheck size={17} color="var(--accent-teal)" strokeWidth={2.25} />
              </div>
              <div>
                <p style={{
                  fontFamily: 'var(--font-heading)',
                  fontWeight: 700,
                  fontSize: '0.78rem',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'var(--text-muted)',
                  margin: '0 0 0.3rem',
                }}>
                  {t('cookie_consent.title')}
                </p>
                <p style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.88rem',
                  color: 'var(--text-secondary)',
                  lineHeight: 1.6,
                  margin: 0,
                }}>
                  {t('cookie_consent.desc')}{' '}
                  <a
                    href="/politica-privacidad"
                    style={{
                      color: 'var(--text-primary)',
                      textDecoration: 'underline',
                      textDecorationColor: 'rgba(255,255,255,0.25)',
                      textUnderlineOffset: '2px',
                    }}
                  >
                    {t('cookie_consent.privacy_link')}
                  </a>
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={decline}
                style={{
                  padding: '0.6rem 1rem',
                  borderRadius: 980,
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--text-secondary)',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.82rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'color 0.2s ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-primary)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
              >
                {t('cookie_consent.decline')}
              </button>
              <button
                onClick={accept}
                style={{
                  padding: '0.6rem 1.4rem',
                  borderRadius: 980,
                  border: 'none',
                  background: 'linear-gradient(135deg, var(--accent-teal) 0%, var(--accent-purple) 100%)',
                  color: '#04110f',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.82rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  boxShadow: '0 10px 26px -8px rgba(0,229,209,0.45)',
                  transition: 'filter 0.2s ease, box-shadow 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.filter = 'brightness(1.08)';
                  e.currentTarget.style.boxShadow = '0 12px 30px -8px rgba(0,229,209,0.6)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.filter = 'brightness(1)';
                  e.currentTarget.style.boxShadow = '0 10px 26px -8px rgba(0,229,209,0.45)';
                }}
              >
                {t('cookie_consent.accept')}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
